import * as angular from 'angular';
import * as _ from 'lodash';

import { ComponentDefinition, IdentifierType, Group, TagDefinition, Identifier, ReconciliationResult, IValueViewModel, Dictionary } from '../../../models';
import { ITagDefinitionService, IIdentifierVmService, ILoggerService, IGroupService } from '../../../services';
import { IRecVM } from './RecVM';
import { guidEmpty, groupScriptUpdated } from '../../../common';

class ReconciliationMeasureTableController implements angular.IComponentController {
    form: angular.IFormController;
    isLoading: boolean;
    displayed: IRecVM[];
    group: Group;
    results: ReconciliationResult[];

    static $inject = [
        '$scope',
        '$q',
        'tagDefinitionService',
        'identifierVmService',
        'loggerService',
        'groupService'
    ];

    constructor(
        private _$scope: angular.IScope,
        private _$q: angular.IQService,
        private _tagDefinitionService: ITagDefinitionService,
        private _identifierVmService: IIdentifierVmService,
        private _loggerService: ILoggerService,
        private _groupService: IGroupService) {
    }

    $onInit() {
        this._$scope.$watch(() => this.group, newValue => {
            if (newValue) {
                this.refresh();
            }
        });

        this._$scope.$on(groupScriptUpdated, () => this.refresh());
        this._$scope.$watch(() => this.results, () => {
            this.displayed = this.refreshResults(angular.copy(this.displayed));
        });
    }

    refreshResults(displayed: IRecVM[]): IRecVM[] {
        if (this.results && displayed) {
            // create index
            const indexResult: { [guid: string]: ReconciliationResult } = {};
            this.results.forEach(r => indexResult[r.guid] = r);
            // attach result to each
            displayed.forEach(d => {
                const guid = d.tag && d.tag.guid && d.tag.guid !== guidEmpty
                    ? d.tag.guid
                    : d.guid;
                const res = indexResult[guid];
                if (res) {
                    d.penalty = res.penalty;
                    d.value = res.averageValue;
                }
            });
            displayed = displayed.sort((a, b) => b.penalty - a.penalty);
        }
        return displayed;
    }

    refresh() {
        const reconciled = this.group.reconciliationMatrix.map(r => r.guid);
        this._identifierVmService.getAllViewModels().then(viewModels => {
            // Create indexes
            const index: Dictionary<IValueViewModel> = {};
            viewModels.forEach(vm => index[vm.guid] = vm);
            const nameIndex: Dictionary<IValueViewModel> = {};
            viewModels.filter(vm => vm.tagName).forEach(vm => nameIndex[vm.tagName] = vm);

            // for each reconciled
            const displayed = reconciled.map(r => index[r] as IRecVM);
            displayed.forEach(vm => {
                if (vm.tagName) {
                    const tag = nameIndex[vm.tagName];
                    if (tag) {
                        vm.tag = tag;
                        vm.tagDefinitionGuid = tag.guid;
                    } else {
                        vm.tag = { name: vm.tagName, guid: guidEmpty };
                    }
                }
            });
            this.displayed = this.refreshResults(displayed);
        });
    }

    save() {
        this.isLoading = true;
        return this.updateIdentifiers()
            .then(() => this.updateTags())
            .then(() => {
                this._loggerService.logSuccess('Identifiers updated');
                this.form.$setPristine();
            })
            .catch(reason => {
                this._loggerService.logError(`Failed to save identifiers : ${reason.message ? reason.message : reason}`);
            })
            .finally(() => {
                this.isLoading = false;
            });
    }

    private updateIdentifiers(): angular.IPromise<Identifier[][]> {
        const identifiers = this.displayed.filter(d => d.identifierType !== IdentifierType.Tag);
        
        return this._groupService
            .getIdentifiers(identifiers.map(i => i.guid))
            .then(data => {
                // map min/max/resolution from data
                const toUpdate: { [groupGuid: string]: Identifier[] } = {};
                identifiers.forEach(i => {
                    const idt = _.find(data, d => d.guid === i.guid);
                    if (idt) {
                        idt.max = i.max;
                        idt.min = i.min;
                        idt.resolution = i.resolution;
                        idt.resolutionPercentage = i.resolutionPercentage;

                        if (!toUpdate[i.groupList[0].guid]) {
                            toUpdate[i.groupList[0].guid] = [];
                        }

                        toUpdate[i.groupList[0].guid].push(idt);
                    } else {
                        throw `Cannot find identifier ${i.name}`;
                    }
                });

                const promises = Object.keys(toUpdate).map(k => {
                    return this._groupService.updateIdentifiers(k, toUpdate[k]);
                });
                return this._$q.all(promises);
            });
    }

    private updateTags(): angular.IPromise<TagDefinition[]> {
        const tags = this.displayed.filter(d => d.identifierType === IdentifierType.Tag);

        return this._tagDefinitionService
            .getByGuids(tags.map(i => i.tag.guid))
            .then(data => {
                // map min/max/resolution from data
                const toUpdate: TagDefinition[] = [];
                tags.forEach(i => {
                    const tag = _.find(data, d => d.tagDefinition.guid === i.tag.guid);
                    if (tag && tag.tagDefinition) {
                        const t = tag.tagDefinition;
                        t.min = i.min;
                        t.max = i.max;
                        t.resolution = i.resolution;
                        t.resolutionPercentage = i.resolutionPercentage;
                        toUpdate.push(t);
                    } else {
                        throw `Cannot find tag for identifier ${i.name}`;
                    }
                });
                return this._tagDefinitionService.update(toUpdate);
            });
    }
}

export const wtReconciliationMeasureTable: ComponentDefinition = {
    componentId: 'wtReconciliationMeasureTable',
    controller: ReconciliationMeasureTableController,
    template: require('./reconciliationMeasureTable.component.html'),
    controllerAs: 'vm',
    bindings: {
        item: '=',
        form: '=',
        group: '=',
        results: '=',
        isLoading: '=?'
    }
};